'use client';

import { LoadingFallback } from '@order-up/common';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { getFirebaseAuth } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import { useSupportLandingPending } from '../hooks/useSupportLanding';
import { useUserSessionValidation, userAuthKeys } from '../hooks/useUserAuth';
import { getAuthServiceUrl } from '../utils/env';
import { createClientLogger } from '../utils/logger';
import {
    hasExpiredImpersonatorClaim,
    hasLiveImpersonatorClaim,
    readImpersonatorClaim,
} from '../utils/impersonationClaims';
import { refreshSessionCookie } from '../utils/sessionRefresh';
import { isSupportLanding } from '../utils/supportLanding';

const logger = createClientLogger('AuthGuard');

interface AuthGuardProps {
    children: React.ReactNode;
    authServiceUrl?: string;
    fallbackComponent?: React.ComponentType;
    loadingComponent?: React.ComponentType;
}

/**
 * Authentication states for the guard
 */
type AuthState =
    | { status: 'loading' }
    | { status: 'authenticated'; method: 'firebase' | 'session' }
    | { status: 'unauthenticated' }
    | { status: 'redirecting' };

/**
 * Determines the current authentication state.
 *
 * Two rules make this fast AND stable:
 *
 * 1. **Never decide before Firebase has answered.** `authResolved` (see
 *    `AuthSnapshot`) distinguishes "signed out" from "not known yet". Firebase
 *    restores a persisted session asynchronously, so acting on `authUser ===
 *    null` before the listener fires redirects signed-in users to the auth app,
 *    which signs them straight back in — a visible double page-load.
 *
 * 2. **A live Firebase user is enough to render.** The server session probe
 *    runs in the background rather than gating first paint. This is safe
 *    because rendering grants no data: Firestore reads are enforced by rules
 *    against the user's own credentials, and API calls are verified server-side
 *    per request. Only a DEFINITIVE "no session" answer demotes us — and even
 *    then we first try to re-mint the cookie (see below) before concluding the
 *    session was revoked.
 *
 * Session recovery vs revocation: a Firebase user with no server session is
 * usually just an EXPIRED cookie (they last 24h/14d and nothing refreshed them),
 * not a revocation. Signing the user out for that is what produced the
 * "logged out and auto-logged back in every morning" behaviour. We attempt a
 * re-mint from the live Firebase ID token first; only if that fails is it a
 * genuine revocation, and only then do we force a local signout.
 */
function useAuthState() {
    const queryClient = useQueryClient();
    // If a token param is present, TokenHandler is processing it — stay loading
    // TokenHandler removes the param via replaceState once done, so subsequent
    // renders proceed normally. Hoisted above the hook calls so we can disable
    // useUserSessionValidation during the token-handoff window — otherwise it
    // races TokenHandler's POST /auth/signin, finds no session cookie yet, and
    // 401s pointlessly. Once TokenHandler primes the cache via setQueryData,
    // re-render runs with hasTokenParam=false and the hook re-enables to read
    // the primed value (no network call thanks to staleTime).
    const hasTokenParam = typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).has('token');

    // Firebase auth. `authResolved` is false until onAuthStateChanged fires —
    // React Query's `isLoading` cannot express this (initialData makes the
    // query `success` at t=0), which is why it was always false and the
    // "wait for Firebase" branches below were unreachable.
    const { data: authData } = useAuth();
    const { authUser, authResolved } = authData;

    // Server session, probed in parallel and NOT gating first paint. Skipped
    // while TokenHandler owns the session-creation flow.
    const { data: sessionUser, isLoading: isSessionLoading } =
        useUserSessionValidation(!hasTokenParam);

    // NOTE: the `users/{uid}` Firestore read that used to be fetched here was
    // removed. It gated the render (`isFirestoreUserLoading`) but was never
    // read for the auth decision — a whole serial round-trip in front of first
    // paint for nothing. AuthenticatedSelectionGuard still fetches it under the
    // same query key for the callers that genuinely need it.

    const isRecoveringRef = useRef(false);

    /**
     * Re-arms the recovery decision when a LIVE impersonation scope lapses.
     *
     * `'skipped'` is terminal, so without this the liveness check ran exactly
     * once: a scope that was live at that moment kept the guard reporting
     * AUTHENTICATED for as long as the tab stayed open, while `apiClient` — the
     * same shared predicate — stopped sending the act-as header the instant
     * `exp` passed. With no usable cookie behind it, every protected request
     * then failed inside a fully-rendered app: precisely the state this whole
     * change exists to prevent, just reached by clock rather than by reload.
     */
    const scopeExpiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (scopeExpiryTimerRef.current) clearTimeout(scopeExpiryTimerRef.current);
        },
        [],
    );

    /**
     * Tracks the expired-cookie recovery attempt.
     *
     * `'failed'` is the only state that may produce `unauthenticated`. Without
     * this the redirect effect in `useAuthRedirect` — which runs synchronously
     * in the same commit — would navigate away before the asynchronous re-mint
     * ever resolved, so the recovery could never win the race.
     *
     * `'skipped'` means "this session is not SUPPOSED to have a server cookie"
     * — a LIVE impersonation / support-scope session, which authenticates with
     * its Bearer. It is terminal like `'failed'` (so the effect does not
     * re-run), but resolves to AUTHENTICATED rather than unauthenticated. An
     * EXPIRED scope never reaches it: that token is refused by the API as well,
     * so it takes the ordinary failed-re-mint path and signs out.
     */
    const [recovery, setRecovery] = useState<
        'idle' | 'attempting' | 'failed' | 'skipped'
    >('idle');

    // A support-scope landing establishes the Firebase session (signInWithCustomToken)
    // BEFORE the server session query refetches, so there's a window where authUser
    // is set but sessionUser is still null. That is NOT a revocation — suppress the
    // auto-signout below for it (mirrors the hasTokenParam guard).
    //
    // Reads the shared latch rather than `window.location`: the landing strips
    // its params on success, so a URL sniff answers "yes" or "no" depending on
    // when it happens to run. The latch is fixed for the life of the page.
    const supportLandingRef = useRef(isSupportLanding());

    // Firebase auth exists but the server says there is no session. Almost
    // always an expired cookie, not a revocation — so try to re-mint it from
    // the live Firebase ID token BEFORE concluding the session is dead. Only a
    // failed re-mint (user disabled, refresh tokens revoked, no longer
    // authorised) is a real revocation worth a forced local signout.
    useEffect(() => {
        if (
            !authResolved ||
            isSessionLoading ||
            !authUser ||
            sessionUser !== null ||
            hasTokenParam ||
            supportLandingRef.current ||
            isRecoveringRef.current ||
            recovery === 'failed' ||
            recovery === 'skipped'
        ) {
            return;
        }

        isRecoveringRef.current = true;
        setRecovery('attempting');
        logger.debug('Firebase auth present with no server session — attempting session re-mint');

        void (async () => {
            // An impersonation / support-scope session has NO server session
            // cookie by design — it authenticates with its Bearer, and the API
            // refuses to mint a cookie from one (a 60-minute scope inside a
            // 14-day shared cookie outlives its own expiry). The re-mint below
            // is therefore guaranteed to fail, and this branch reads a
            // guaranteed failure as a revocation and signs the admin out
            // mid-session.
            //
            // Distinct from `supportLandingRef`, which covers only the ONE page
            // load that performed the landing; a later reload, or a second
            // management tab, is an ordinary load carrying a live support
            // session.
            //
            // LIVE, not merely present. `impersonator.exp` bounds the scope,
            // and Firebase keeps refreshing the token that carries it long
            // after it lapses — so "has an impersonator claim" is true forever
            // once a support session has run in this browser. Skipping recovery
            // on that would report the app AUTHENTICATED while `apiClient`
            // (same helper) has already stopped trusting the same token and
            // every protected request 401s. An EXPIRED scope must fall through
            // to the normal path instead, where the re-mint fails and the
            // forced sign-out returns the admin to their own identity.
            const tokenResult = await getFirebaseAuth()
                .currentUser?.getIdTokenResult()
                .catch(() => null);
            if (hasLiveImpersonatorClaim(tokenResult)) {
                logger.debug('Live impersonation session has no server cookie — skipping re-mint');
                // Re-arm for the moment the scope lapses. `'skipped'` is
                // terminal by design (the effect must not re-run every render),
                // so the ONLY way this decision gets revisited is if something
                // schedules it — otherwise "live" is latched for the lifetime
                // of the tab and the app outlives its own credential.
                const exp = readImpersonatorClaim(tokenResult?.claims)?.exp;
                if (typeof exp === 'number') {
                    // +1s so the re-run observes an unambiguously past `exp`
                    // rather than racing the boundary. Capped at the largest
                    // delay setTimeout can represent; anything beyond that is a
                    // nonsense claim, and clamping degrades to today's
                    // behaviour rather than firing immediately.
                    const delay = Math.min(
                        Math.max(0, exp * 1000 - Date.now()) + 1_000,
                        2_147_483_647,
                    );
                    if (scopeExpiryTimerRef.current) clearTimeout(scopeExpiryTimerRef.current);
                    scopeExpiryTimerRef.current = setTimeout(() => {
                        logger.info('Impersonation scope reached its expiry — re-evaluating session');
                        // Back to 'idle' re-enables the effect, which now sees
                        // an EXPIRED claim and takes the sign-out path.
                        setRecovery('idle');
                    }, delay);
                }
                setRecovery('skipped');
                return;
            }
            if (hasExpiredImpersonatorClaim(tokenResult)) {
                logger.info('Impersonation scope has expired — ending the local session');
            }

            const recovered = await refreshSessionCookie();
            if (recovered) {
                logger.debug('Session cookie re-minted — no sign-out needed');
                setRecovery('idle');
                await queryClient.invalidateQueries({ queryKey: userAuthKeys.session() });
                return;
            }

            logger.debug('Session re-mint failed — treating as revocation, forcing local signout');
            setRecovery('failed');
            await getFirebaseAuth()
                .signOut()
                .catch((err: Error) => {
                    logger.warn('Failed to force Firebase signout after session revocation', {
                        error: err.message,
                    });
                });
        })()
            .catch(() => {
                setRecovery('failed');
            })
            .finally(() => {
                isRecoveringRef.current = false;
            });
    }, [
        authResolved,
        isSessionLoading,
        authUser,
        sessionUser,
        hasTokenParam,
        recovery,
        queryClient,
    ]);

    if (hasTokenParam) {
        return { status: 'loading' } as const;
    }

    // Rule 1: Firebase has not answered yet — we know nothing. Hold.
    if (!authResolved) {
        return { status: 'loading' } as const;
    }

    if (authUser) {
        // Definitive "no session" from the server. Hold on the loader while the
        // re-mint above is attempted — an expired cookie recovers silently and
        // the user never sees a redirect. Only a FAILED re-mint is a real
        // revocation.
        //
        // A support landing is exempt: it establishes its session out-of-band,
        // so we return 'unauthenticated' immediately and let useAuthRedirect's
        // bounded grace timer decide. Holding there would hang forever if the
        // landing never completes.
        if (sessionUser === null) {
            // An impersonation session legitimately has no server cookie — the
            // live Firebase user IS the credential. Holding here would hang the
            // app on a loader for the whole support session.
            if (recovery === 'skipped') {
                return { status: 'authenticated', method: 'firebase' } as const;
            }
            if (supportLandingRef.current || recovery === 'failed') {
                return { status: 'unauthenticated' } as const;
            }
            return { status: 'loading' } as const;
        }

        // Rule 2: a live Firebase user is enough to render. Whether the probe
        // has succeeded, is still in flight, or could not reach the API at all
        // (`sessionUser === undefined`), it continues in the background and can
        // only demote us via the definitive branch above. This is what removes
        // `/auth/session` from the critical path.
        return { status: 'authenticated', method: 'firebase' } as const;
    }

    // No Firebase user, but a valid server session — cookie-only auth is still
    // authentication (e.g. a tab where Firebase persistence was cleared).
    if (sessionUser) {
        return { status: 'authenticated', method: 'session' } as const;
    }

    // No Firebase user and the session probe hasn't finished — keep loading.
    if (isSessionLoading) {
        return { status: 'loading' } as const;
    }

    // All checks complete, no auth found
    return { status: 'unauthenticated' } as const;
}


/**
 * A support-scope landing (`?support=1`) establishes the session asynchronously
 * on a cross-origin tab: SupportScopeLanding redeems the handoff then
 * signInWithCustomToken. On a COLD load with no pre-existing session (e.g. a
 * fresh/incognito browser), this guard would otherwise see `unauthenticated`
 * and instantly bolt to the auth service — which forwards the profile-less
 * admin to onboarding — all before the landing finishes. We give the landing a
 * bounded grace window to flip auth to `authenticated`.
 */
const SUPPORT_LANDING_GRACE_MS = 8000;

/**
 * Handles immediate redirect to auth service when unauthenticated
 * Returns redirecting status immediately to prevent any UI flash
 */
function useAuthRedirect(authState: AuthState, authServiceUrl?: string): Exclude<AuthState, { status: 'unauthenticated' }> {
    const hasRedirectedRef = useRef(false);

    // Shared latch — fixed for the life of the page, unlike the URL it was
    // derived from (the landing strips its params on success).
    const supportLandingRef = useRef(isSupportLanding());
    const supportLandingPending = useSupportLandingPending();
    const [graceElapsed, setGraceElapsed] = useState(!supportLandingRef.current);

    useEffect(() => {
        if (!supportLandingRef.current) return;
        const timer = setTimeout(() => setGraceElapsed(true), SUPPORT_LANDING_GRACE_MS);
        return () => clearTimeout(timer);
    }, []);

    // Hold (show loading) instead of redirecting while a support landing is
    // mid-flight. Once the landing signs in, authState flips to 'authenticated'
    // and children render; if it never does, the grace timer lets us fall
    // through to the normal redirect. Holding only ever renders the loader —
    // never children — so this opens no unauthenticated access.
    //
    // Two conditions, and the union is deliberate:
    //   - the landing has not settled yet. This is the real signal, and it is
    //     bounded by the landing's own watchdog. The wall-clock timer alone
    //     bounced a landing that legitimately took longer than 8s (a cold API
    //     instance, a slow redeem) into a redirect that discarded it.
    //   - the grace window has not elapsed. Still needed AFTER the landing
    //     settles: `signInWithCustomToken` has resolved but the auth listener
    //     has not necessarily propagated, so `authState` can read
    //     'unauthenticated' for a beat on a browser with no prior session.
    const holdForSupport =
        supportLandingPending || (supportLandingRef.current && !graceElapsed);

    useEffect(() => {
        if (authState.status === 'unauthenticated' && !holdForSupport && !hasRedirectedRef.current) {
            hasRedirectedRef.current = true;
            logger.debug('No authentication found, redirecting to auth service');

            const finalAuthUrl = authServiceUrl || getAuthServiceUrl() || 'http://localhost:3000';
            const currentUrl = window.location.href;
            const authUrl = `${finalAuthUrl}?returnUrl=${encodeURIComponent(currentUrl)}`;

            logger.debug('Redirecting to auth service', { authUrl });

            // Immediate redirect without timeout
            window.location.href = authUrl;
        }
    }, [authState.status, authServiceUrl, holdForSupport]);

    // Return redirecting status as soon as we detect unauthenticated to prevent
    // UI flash — unless a support landing is mid-flight, in which case hold on
    // the loader so the session has time to establish.
    if (authState.status === 'unauthenticated') {
        return holdForSupport
            ? ({ status: 'loading' } as const)
            : ({ status: 'redirecting' } as const);
    }

    return authState;
}

export function AuthGuard({
    children,
    authServiceUrl,
    fallbackComponent: _FallbackComponent,
    loadingComponent: LoadingComponent = LoadingFallback
}: AuthGuardProps) {
    const authState = useAuthState();
    const finalAuthState = useAuthRedirect(authState, authServiceUrl);

    // Render based on auth state
    switch (finalAuthState.status) {
        case 'loading':
        case 'redirecting':
            return <LoadingComponent />;

        case 'authenticated':
            return <>{children}</>;

        // Note: 'unauthenticated' is converted to 'redirecting' by useAuthRedirect
        // so this case should never occur, but TypeScript needs it for completeness

        default:
            return <LoadingComponent />;
    }
}
